import { Link } from 'react-router-dom';

const INK = '#0f172a';
const MUTED = '#64748b';
const INDIGO = '#6366f1';

const UPDATED = 'September 16, 2026';

function H2({ children }) {
  return <h2 style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.3px', margin: '36px 0 12px' }}>{children}</h2>;
}
function P({ children }) {
  return <p style={{ fontSize: 15.5, color: '#334155', lineHeight: 1.75, margin: '0 0 14px' }}>{children}</p>;
}

function Privacy() {
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', color: INK, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <nav style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: 800, margin: '0 auto', padding: '18px 32px' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', fontWeight: 800, fontSize: 16, color: INK }}>
            <span style={{ display: 'inline-flex', width: 26, height: 26, borderRadius: 7, background: `linear-gradient(135deg, ${INDIGO}, #a855f7)`, alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </span>
            LE Tech Tools
          </Link>
          <Link to="/tools" style={{ color: INDIGO, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>All tools →</Link>
        </nav>
      </div>

      <main style={{ maxWidth: 800, margin: '0 auto', padding: '44px 32px 80px' }}>
        <h1 style={{ fontSize: 'clamp(28px, 4.5vw, 40px)', fontWeight: 800, letterSpacing: '-0.6px', margin: '0 0 8px' }}>
          Privacy Policy
        </h1>
        <p style={{ fontSize: 14, color: '#94a3b8', margin: '0 0 28px' }}>Last updated {UPDATED}</p>

        {/* TL;DR */}
        <div style={{
          background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '18px 22px', marginBottom: 12,
        }}>
          <p style={{ fontSize: 15, color: '#15803d', lineHeight: 1.7, margin: 0 }}>
            <strong>The short version:</strong> every tool on this site processes your files, photos, and documents{' '}
            <strong>entirely in your browser</strong>. We do not receive, store, or see that data. The two exceptions
            are the <strong>Video Canvass Tracker</strong> and the <strong>Area Search Coordinator</strong>, which are
            intentionally collaborative and store what you type into them — and, for Area Search, your live location
            while you choose to share it. See below for the full detail.
          </p>
        </div>
        <p style={{ fontSize: 13.5, color: MUTED, margin: '0 0 8px' }}>
          Want the technical proof, not just the policy? See <Link to="/code-validation" style={{ color: INDIGO }}>how we verify this</Link>.
        </p>

        <H2>What we don&rsquo;t collect</H2>
        <P>
          LE Tech Tools is a set of browser-based utilities for law enforcement and investigative work — LPR analysis,
          warrant/subpoena-return parsers, time converters, an EXIF viewer, an image (HEIC) converter, document generators, and more. For every one
          of these tools, the file, photo, or document you open is <strong>read and processed locally in your browser</strong>.
          It is never uploaded, transmitted, or visible to us. Once the page has loaded, you can disconnect from the
          internet entirely and these tools keep working — that is a deliberate design choice, not a coincidence.
        </P>
        <P>
          This means we never receive: warrant or subpoena return files, license-plate reader data, call-detail records,
          photos (including HEIC photos you convert) or their EXIF metadata, generated preservation requests or subpoena riders, or any other case-related
          content you open in these tools.
        </P>

        <H2>Tools that use the network</H2>
        <P>
          Five tools are different by design. The first two each give a team one shared, live map, which inherently
          requires a server; both use the same access model — no login, just a private, hard-to-guess link. Anyone
          with that link can view and edit, so treat the link itself as sensitive and only share it with your team.
          Map Search queries public OpenStreetMap services for map features, IP and Domain Intelligence looks up
          public information about IP addresses and domains from outside sources, and the VIN Decoder can optionally
          send a VIN to NHTSA&rsquo;s public vehicle database.
        </P>

        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 10px' }}>Video Canvass Tracker</h3>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            The canvass title, subject description, and each pin you add (address, coordinates, notes, who pulled the
            footage, contact outcome, and review result) are stored in our Firestore database (Google Cloud).
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            If you type an address instead of dropping a pin on the map, that address text is sent to
            OpenStreetMap&rsquo;s Nominatim service to be converted into coordinates, and the interactive map (and the
            PDF export&rsquo;s map image) loads map tiles from OpenStreetMap.
          </li>
        </ul>

        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 10px' }}>Area Search Coordinator</h3>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            <strong>Live location sharing is opt-in and continuous while active.</strong> Tapping &ldquo;Start Sharing
            My Location&rdquo; sends your device&rsquo;s GPS position to our Firestore database in real time,
            throttled to roughly one update every 8 seconds or 15 meters of movement, for as long as sharing is on.
            It is visible immediately to anyone who has the search&rsquo;s link. Tapping &ldquo;Stop Sharing,&rdquo;
            closing the tab, or navigating away stops new updates — it does <em>not</em> delete the trail already
            recorded, which stays visible to link-holders.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            The search title, vehicle/subject description, search-zone boundary, your chosen display name, and any
            pins you drop (coordinates, notes, and a reverse-geocoded address) are stored in the same Firestore
            database.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            Dropping a pin looks up its address via OpenStreetMap&rsquo;s Nominatim service, and the interactive map
            loads map tiles from OpenStreetMap — the same as the Video Canvass Tracker.
          </li>
        </ul>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 10px' }}>Map Search</h3>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            Map Search loads OpenStreetMap map tiles. If you use its place finder, the place text is sent to
            OpenStreetMap&rsquo;s Nominatim service. When you run a search, the selected rectangle, circle, or polygon
            and the OpenStreetMap feature tags you selected are sent to Overpass (with a public Overpass mirror used
            only if the primary service is unavailable). LE Tech Tools does not store those searches.
          </li>
        </ul>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 10px' }}>IP and Domain Intelligence</h3>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            <strong>Nothing you paste is stored by us, ever</strong> — this tool has no database and writes nothing to
            the cloud. It is listed here only because it can send an IP address to outside services.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            <strong>Screening is confidential.</strong> The VPN, datacenter, Tor and abuse-blocklist checks run against
            public lists downloaded in full and cached in your own browser, so the addresses you paste are never
            transmitted. Those lists come from{' '}
            <a href="https://github.com/X4BNet/lists_vpn" target="_blank" rel="noopener noreferrer" style={{ color: INDIGO }}>X4BNet/lists_vpn</a>,{' '}
            <a href="https://github.com/stamparm/ipsum" target="_blank" rel="noopener noreferrer" style={{ color: INDIGO }}>IPsum</a>, and the{' '}
            <a href="https://metrics.torproject.org/onionoo.html" target="_blank" rel="noopener noreferrer" style={{ color: INDIGO }}>Tor Project&rsquo;s Onionoo</a>{' '}
            service, and are refreshed about once a day.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            <strong>Live lookup is opt-in per address.</strong> Only when you press the live-lookup button for a
            specific address is that address sent — to RDAP servers at the regional internet registries (via
            rdap.org), dns.google, ipwho.is, freeipapi.com, ipapi.is, internetdb.shodan.io, and stat.ripe.net. Those
            providers receive the IP you are researching and your own network connection&rsquo;s IP, and handle them
            under their own privacy policies. The tool lists every recipient in the interface before you click.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            <strong>Domain and email lookups always use the network</strong>, as there is no offline equivalent for
            them. The domain is sent to rdap.org (which forwards to the relevant domain registry) and to dns.google
            to read its mail servers, nameservers and SPF/DMARC records. If you paste an <strong>email address, only
            the domain part is ever sent</strong> — the mailbox name stays on your device.
          </li>
        </ul>
        <h3 style={{ fontSize: 17, fontWeight: 700, margin: '20px 0 10px' }}>VIN Decoder</h3>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            <strong>Checking a VIN&rsquo;s format, check digit, country of origin, and likely manufacturer never
            uses the network</strong> — all of that is computed locally as you type. Only pressing the separate
            <strong> Full decode</strong> button sends the VIN — nothing else — to{' '}
            <a href="https://vpic.nhtsa.dot.gov/api/" target="_blank" rel="noopener noreferrer" style={{ color: INDIGO }}>
              NHTSA&rsquo;s free public vPIC vehicle database
            </a>{' '}
            for the exact make, model, engine, and manufacturing plant.
          </li>
        </ul>
        <P>
          No other tool on this site stores or transmits any case data, and no tool ever tracks your location unless
          you are actively using the Area Search Coordinator and have explicitly tapped Start Sharing. Everything
          else stays on your device.
        </P>

        <H2>AI features that download a model</H2>
        <P>
          Three features run an AI model: the <strong>Audio &amp; Video Transcription</strong> tool (including its
          optional speaker identification), and the optional message translation in the <strong>Facebook</strong> and{' '}
          <strong>Instagram Warrant Processors</strong>. The model runs <strong>inside your browser</strong>, not on a
          server. Your recording and your message text are processed on your own device and are never uploaded. These
          features are listed here only because the model file itself has to be downloaded to you before it can run.
        </P>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            <strong>What gets downloaded.</strong> The first time you use one of these features, your browser fetches
            the model file — roughly 40&nbsp;MB to 250&nbsp;MB, depending on the transcription size you choose, plus
            about 33&nbsp;MB more if you turn on speaker identification — from the Hugging Face model hub
            (huggingface.co), along with the WebAssembly runtime that executes it from the jsDelivr CDN
            (cdn.jsdelivr.net). Your browser caches both, so later runs need no network at all: once the model has
            loaded you can disconnect from the internet and keep transcribing.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            <strong>What those providers see.</strong> An ordinary file request — your network connection&rsquo;s IP
            address and which model you asked for. They receive nothing about your recording, your case, or what the
            model produced, and we send them nothing on your behalf.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            <strong>Your audio never leaves your device.</strong> The file you choose is decoded and transcribed
            entirely in your browser, and the transcript exists only there until you copy or export it. We never
            receive the audio, the video, the message text, or the transcript, and we do not store any of them.
          </li>
        </ul>

        <H2>Fallback decoder for unusual audio formats</H2>
        <P>
          The <strong>Audio &amp; Video Transcription</strong> and <strong>Audio Format Converter</strong> tools read
          most audio directly in your browser. Files using the codec some inmate-call recording systems label
          &ldquo;ICMV&rdquo; — actually the standard G.729 speech codec — are decoded by a small decoder built into
          this site itself (under 50&nbsp;KB), so nothing is downloaded for those files at all. A smaller number of
          files use a codec or container that decoder doesn&rsquo;t cover either. For those only, your browser
          downloads a general-purpose decoder (<strong>ffmpeg.wasm</strong>, a one-time ~25&nbsp;MB download from the
          jsDelivr CDN, cached afterward) and uses it to convert the file locally. The audio itself is never
          uploaded at any point — only the decoder software is fetched, the same way the AI models above are.
        </P>

        <H2>Analytics</H2>
        <P>
          We collect lightweight, anonymous usage data so we know which tools are useful — never the content you put
          into them. Specifically:
        </P>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            <strong>Tool usage counts</strong> — which tool page was opened and, separately, when its main action was
            used (e.g. a file was analyzed) — stored in our own database as a tool name and a timestamp. No file names,
            file contents, or case data are ever included.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            <strong>Google Analytics</strong> — standard, aggregate web analytics (pages visited, approximate device/
            browser info, and general location inferred from IP address) provided by Google. Google&rsquo;s handling of
            this data is governed by the{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: INDIGO }}>
              Google Privacy Policy
            </a>. Many browsers and ad blockers block this by default, which is fine — every tool works regardless.
          </li>
        </ul>

        <H2>What stays only on your device</H2>
        <P>
          A couple of tools remember a few non-sensitive preferences locally in your browser (via{' '}
          <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 13.5 }}>localStorage</code>),
          so you don&rsquo;t have to retype them:
        </P>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            The <strong>Subpoena Rider Generator</strong> can optionally remember your name, title, email, and phone
            number for the requester fields.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}>
            The <strong>Video Canvass Tracker</strong> remembers the last name you entered as who pulled a location&rsquo;s
            footage.
          </li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}>
            The <strong>Area Search Coordinator</strong> remembers a random participant id and color per search (so
            reloading the page keeps your same trail instead of starting a new one) and the last display name you
            entered, to suggest it next time.
          </li>
        </ul>
        <P>
          This information is saved only in your own browser and is never sent anywhere by us — with one exception:
          if you use it to fill in a Video Canvass or Area Search field, that field is saved to the shared search/
          canvass as described above. You can clear it anytime by clearing the field in the tool or clearing your
          browser&rsquo;s site data.
        </P>

        <H2>Accounts &amp; cookies</H2>
        <P>
          There are no user accounts, sign-ups, or logins anywhere on this site. We do not set first-party tracking
          cookies. If Google Analytics is not blocked in your browser, it may set its own cookies per Google&rsquo;s policy
          linked above.
        </P>

        <H2>Third-party services</H2>
        <P>This site relies on a small number of infrastructure providers:</P>
        <ul style={{ paddingLeft: 20, margin: '0 0 14px' }}>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}><strong>Google Firebase / Google Cloud</strong> — hosts this site and stores Video Canvass data, Area Search data (including live location while a participant is sharing), and anonymous usage counts.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}><strong>Google Analytics</strong> — anonymous, aggregate site usage statistics.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75, marginBottom: 6 }}><strong>OpenStreetMap / Overpass</strong> — map tiles for the Video Canvass Tracker, Area Search Coordinator, Case Timeline Builder, Map Search, and Ping Mapper; address geocoding for Video Canvass and Area Search; and place finding plus map-feature queries for Map Search. Map Search sends its selected shape and OSM feature tags to Nominatim and Overpass; it uses a public Overpass mirror if the primary endpoint is unavailable. The Case Timeline Builder and Ping Mapper only load map tiles for the map they display — neither ever sends the address, coordinate, or radius you enter anywhere; that stays on your device like everything else in those tools.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}><strong>IP intelligence sources</strong> — used only by the IP and Domain Intelligence tool, and only as described above: GitHub (raw.githubusercontent.com) and the Tor Project (onionoo.torproject.org) for the public blocklists it caches for offline screening, and rdap.org, the five regional internet registries, dns.google, ipwho.is, freeipapi.com, ipapi.is, internetdb.shodan.io and stat.ripe.net for opt-in per-address lookups.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}><strong>Hugging Face &amp; jsDelivr</strong> — deliver the in-browser AI models (huggingface.co) and WebAssembly runtimes (cdn.jsdelivr.net) used by the Audio &amp; Video Transcription tool, the optional translation in the Facebook and Instagram Warrant Processors, and — for jsDelivr specifically — the fallback audio decoder (ffmpeg.wasm) that the Transcription and Audio Format Converter tools download only for files that need it. They receive a request for the file and your IP address; they never receive your audio, video, message text, or results, and they are contacted only when you use one of those features.</li>
          <li style={{ fontSize: 15, color: '#334155', lineHeight: 1.75 }}><strong>NHTSA (vpic.nhtsa.dot.gov)</strong> — used only by the VIN Decoder tool's optional Full decode button. Receives the VIN you enter and your IP address; never contacted unless you press that button.</li>
        </ul>
        <P>No other outbound requests are made by this site.</P>

        <H2>Data retention &amp; removal</H2>
        <P>
          Video Canvass and Area Search data persists so your team can keep working a case over time; individual
          Video Canvass pins and Area Search pins can be deleted from within their respective tools at any time.
          Area Search location trails are not editable or deletable by participants once recorded (they are kept as
          a historical record of what was covered). If you need a canvass or search fully removed, or have any other
          privacy question, see the contact section below.
        </P>

        <H2>Changes to this policy</H2>
        <P>
          If this policy changes, we&rsquo;ll update the date at the top of this page. Continued use of the site after a
          change means you accept the updated policy.
        </P>

        <H2>Contact</H2>
        <P>
          Questions about this policy, or a request to remove Video Canvass or Area Search data, can be directed to
          the site administrator.
        </P>
      </main>
    </div>
  );
}

export default Privacy;
